import { QueryClient } from "@tanstack/react-query";
import { parseSwapPartnerId } from "./components/ChangesByItem/manualSignalCr";
import { reviewKeys } from "./queryKeys";
import { ReviewChangeRequest } from "./types";

/** Item ids whose summary/inspector caches should refresh after a CR mutation. */
export const collectCrAffectedItemIds = (
  cr: ReviewChangeRequest,
): string[] => {
  const ids = new Set<string>();
  if (cr.flyerItemId) ids.add(cr.flyerItemId);
  if (String(cr.fieldKey) === "swapPosition") {
    const partnerId = parseSwapPartnerId(cr.newValue);
    if (partnerId) ids.add(partnerId);
  }
  return Array.from(ids);
};

export const invalidateFlyerItemCaches = async (
  queryClient: QueryClient,
  itemIds: string[],
): Promise<void> => {
  const unique = Array.from(new Set(itemIds.filter(Boolean)));
  if (unique.length === 0) return;
  await Promise.all(
    unique.flatMap((id) => [
      queryClient.invalidateQueries({ queryKey: reviewKeys.itemSummary(id) }),
      queryClient.invalidateQueries({
        queryKey: reviewKeys.itemInspector(id),
      }),
    ]),
  );
};

export const invalidateFlyerMetaCaches = async (
  queryClient: QueryClient,
  flyerId: string,
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: reviewKeys.bootstrap(flyerId) }),
    queryClient.invalidateQueries({ queryKey: reviewKeys.snapshot(flyerId) }),
  ]);
};

/** Extract affected item ids from a socket CR event (`{ changeRequest }`). */
export const parseSocketCrAffectedItemIds = (payload: unknown): string[] => {
  if (!payload || typeof payload !== "object") return [];
  const envelope = payload as { changeRequest?: unknown };
  const cr = envelope.changeRequest;
  if (!cr || typeof cr !== "object") return [];
  return collectCrAffectedItemIds(cr as ReviewChangeRequest);
};
